/**
 * Routes Resolver - Logique commune de résolution de routes
 * Module partagé entre frontend (api-router.js) et workers (worker-fetch.js, etc.)
 * 
 * Fonctionne de concert avec routes-config-base.js qui fournit la configuration de base.
 * Intègre workers-config.js pour centraliser les URLs des workers.
 * 
 * Responsabilités:
 * - Déterminer quel worker utiliser basé sur l'endpoint
 * - Retourner la configuration du worker (baseUrl, name, circuitRoute)
 * - Valider les endpoints
 * - Fournir des helpers pour construire des configs enrichies
 * - Récupérer automatiquement les URLs depuis workers-config.js
 */

// Importer depuis workers-config.js (source unique de vérité)
import { getWorkersConfig } from './workers-config.js';

/**
 * Construire les baseUrls automatiquement depuis workers-config.js
 * Élimine le besoin de dupliquer manuellement les URLs
 * 
 * ATTENTION: Cette fonction est synchrone pour compatibilité avec les cas d'usage existants.
 * Elle cache les URLs des workers pour éviter les imports répétés.
 * 
 * @param {string} [env='auto'] - Environnement (auto/development/production)
 * @returns {Object} Objet {ia, wa, config, chat} avec URLs
 * 
 * @example
 * import { getWorkersBaseUrls } from './routes-resolver.js';
 * 
 * const baseUrls = getWorkersBaseUrls('auto');
 * // { ia: 'http://localhost:65432', wa: 'http://localhost:64033', ... }
 */

// Cache pour les URLs des workers (évite les imports répétés)
let workersBaseUrlsCache = null;

export function getWorkersBaseUrls(env = 'auto') {
  // Retourner du cache si disponible et env === 'auto'
  if (env === 'auto' && workersBaseUrlsCache) {
    return workersBaseUrlsCache;
  }

  // Récupérer la config depuis workers-config.js (source unique)
  const config = getWorkersConfig(env);
  
  if (!config) {
    console.error('[getWorkersBaseUrls] ERROR: getWorkersConfig returned null or undefined');
    return {
      ia: null,
      wa: null,
      config: null,
      chat: null
    };
  }
  
  const result = {
    ia: config.IA_PROXY,
    wa: config.WA_PROXY,
    config: config.CONFIG,
    chat: config.CHATBOT
  };

  // Mettre en cache
  if (env === 'auto') {
    workersBaseUrlsCache = result;
  }

  return result;
}

/**
 * STRUCTURE DE ROUTE_CONFIG
 * ========================
 * 
 * Les routes sont définies comme un tableau d'objets avec la structure suivante :
 * 
 * ```javascript
 * const ROUTE_CONFIG = [
 *   {
 *     route: '/api/ia/text',           // Préfixe de route à matcher
 *     baseUrl: 'http://localhost:65432',  // URL de base du worker
 *     workerName: 'IA-Proxy Worker - Text', // Nom du worker
 *     type: 'TEXT',                    // Type de route (optionnel, frontend)
 *     apiKeySource: 'text_ai_api_key', // Source de clé API (optionnel, worker)
 *     circuitRoute: '/api/ia/text'     // Route du circuit-breaker (optionnel, frontend)
 *   },
 *   {
 *     route: '/api/config/',
 *     baseUrl: 'http://localhost:63270',
 *     workerName: 'Config Worker',
 *     // ... autres propriétés optionnelles
 *   }
 * ];
 * ```
 * 
 * ALGORITHME DE RÉSOLUTION
 * ========================
 * 
 * Quand on appelle resolveWorkerConfig('/api/ia/text/generate', ROUTE_CONFIG) :
 * 
 * 1. TRIER les routes par longueur décroissante (pour matcher les plus spécifiques d'abord)
 *    - '/api/ia/text' (14 chars) avant '/api/ia' (7 chars)
 * 
 * 2. PARCOURIR les routes triées
 *    - Vérifier si endpoint.startsWith(route.route)
 *    - '/api/ia/text/generate'.startsWith('/api/ia/text') ✅ MATCH
 * 
 * 3. CONSTRUIRE le résultat avec :
 *    - baseUrl (toujours inclus)
 *    - workerName (toujours inclus)
 *    - circuitRoute (si défini dans la config)
 *    - apiKeySource (si défini dans la config)
 * 
 * EXEMPLE CONCRET
 * ===============
 * 
 * Frontend (ROUTES_CONFIG) :
 * ```javascript
 * {
 *   route: '/api/ia/text',
 *   baseUrl: 'http://localhost:65432',
 *   workerName: 'IA-Proxy Worker - Text',
 *   type: 'TEXT',
 *   circuitRoute: '/api/ia/text'  // ← Inclus dans la réponse
 * }
 * 
 * resolveWorkerConfig('/api/ia/text/generate', ROUTES_CONFIG)
 * // Retourne:
 * // {
 * //   baseUrl: 'http://localhost:65432',
 * //   workerName: 'IA-Proxy Worker - Text',
 * //   circuitRoute: '/api/ia/text'  // ← Pour le circuit-breaker
 * // }
 * ```
 * 
 * Worker (ROUTE_CONFIG) :
 * ```javascript
 * [
 *   {
 *     route: '/api/config/',
 *     baseUrl: 'http://localhost:63270',
 *     workerName: 'wab-config',
 *     apiKeySource: 'WORKER_API_KEY'  // ← Inclus dans la réponse
 *   }
 * ]
 * 
 * resolveWorkerConfig('/api/config/read/languages', ROUTE_CONFIG)
 * // Retourne:
 * // {
 * //   baseUrl: 'http://localhost:63270',
 * //   workerName: 'wab-config',
 * //   apiKeySource: 'WORKER_API_KEY'  // ← Pour les headers
 * // }
 * ```
 * 
 * Utilisation en frontend:
 * ```js
 * import { resolveWorkerConfig } from '../commons/routes-resolver.js';
 * const workerConfig = resolveWorkerConfig(endpoint, ROUTES_CONFIG);
 * // workerConfig.baseUrl + endpoint → URL complète
 * // workerConfig.circuitRoute → Pour le circuit-breaker
 * ```
 * 
 * Utilisation en worker:
 * ```js
 * import { resolveWorkerConfig } from '../../commons-shared/routes-resolver.js';
 * const targetConfig = resolveWorkerConfig(endpoint, ROUTE_CONFIG);
 * // targetConfig.baseUrl + endpoint → URL complète
 * // targetConfig.apiKeySource → Pour les headers d'authentification
 * ```
 */

/**
 * Résoudre la configuration du worker basée sur l'endpoint
 * 
 * @param {string} endpoint - L'endpoint API (ex: '/api/ia/text/generate', '/api/config/languages')
 * @param {Array<Object>} routesConfig - Configuration des routes (ROUTES_CONFIG ou variante worker)
 * @returns {Object|null} Configuration du worker {baseUrl, workerName, circuitRoute} ou null
 * 
 * @example
 * // Frontend usage with ROUTES_CONFIG
 * const config = resolveWorkerConfig(
 *   '/api/ia/text/generate',
 *   ROUTES_CONFIG
 * );
 * // Returns: { baseUrl: 'http://localhost:65432', workerName: 'IA-Proxy Worker - Text', circuitRoute: '/api/ia/text' }
 * 
 * @example
 * // Worker usage with ROUTE_CONFIG
 * const config = resolveWorkerConfig(
 *   '/api/config/read/languages',
 *   Object.entries(ROUTE_CONFIG).map(([route, cfg]) => ({ route, ...cfg }))
 * );
 * // Returns: { baseUrl: 'http://localhost:63270', workerName: 'wab-config' }
 */
export function resolveWorkerConfig(endpoint, routesConfig) {
  if (!endpoint || typeof endpoint !== 'string') {
    return null;
  }

  if (!routesConfig || !Array.isArray(routesConfig)) {
    return null;
  }

  // Chercher la route qui correspond au début de l'endpoint
  // Trier par longueur décroissante pour matcher les routes plus spécifiques d'abord
  const sortedRoutes = [...routesConfig].sort((a, b) => {
    const routeA = a.route || '';
    const routeB = b.route || '';
    return routeB.length - routeA.length;
  });

  for (const config of sortedRoutes) {
    const route = config.route || '';
    if (endpoint.startsWith(route)) {
      // Retourner au format frontend ou worker selon les propriétés disponibles
      const result = {
        route: config.route,  // IMPORTANT: Inclure la route pour resolveByBase
        baseUrl: config.baseUrl,
        workerName: config.workerName
      };

      // Ajouter circuitRoute si disponible (format frontend)
      if (config.circuitRoute !== undefined) {
        result.circuitRoute = config.circuitRoute;
      }

      // Ajouter apiKeySource si disponible (format worker)
      if (config.apiKeySource !== undefined) {
        result.apiKeySource = config.apiKeySource;
      }

      return result;
    }
  }

  return null;
}

/**
 * Obtenir la configuration d'une route par son type (pour conversions type <-> route)
 * Utile pour les workers qui ont besoin de mapper un type à une route
 * 
 * @param {string} type - Le type de route (TEXT, VISION, TRANSLATION, WA, CONFIG, CHAT)
 * @param {Array<Object>} routesConfig - Configuration des routes
 * @returns {Object|null} La configuration de la route ou null
 * 
 * @example
 * const config = resolveRouteByType('TEXT', ROUTES_CONFIG);
 * // Returns configuration for /api/ia/text
 */
export function resolveRouteByType(type, routesConfig) {
  if (!type || !routesConfig || !Array.isArray(routesConfig)) {
    return null;
  }

  return routesConfig.find(config => config.type === type) || null;
}

/**
 * Valider qu'un endpoint est supporté par la configuration des routes
 * 
 * @param {string} endpoint - L'endpoint API
 * @param {Array<Object>} routesConfig - Configuration des routes
 * @returns {boolean} true si l'endpoint est valide, false sinon
 * 
 * @example
 * const isValid = isValidEndpoint('/api/ia/text/generate', ROUTES_CONFIG);
 * // Returns: true
 */
export function isValidEndpoint(endpoint, routesConfig) {
  return resolveWorkerConfig(endpoint, routesConfig) !== null;
}

/**
 * Extraire le type de route de l'endpoint (optionnel, pour debug/logging)
 * 
 * @param {string} endpoint - L'endpoint API
 * @returns {string|null} Le type extrait de l'endpoint ou null
 * 
 * @example
 * const type = extractRouteType('/api/ia/text/generate');
 * // Returns: 'text'
 */
export function extractRouteType(endpoint) {
  if (!endpoint) return null;

  // Pattern: /api/{service}/{type}/...
  const match = endpoint.match(/^\/api\/([^/]+)\/([^/]+)/);
  if (match) {
    return match[2]; // 'text', 'vision', 'translation', etc.
  }

  return null;
}

/**
 * Construire un endpoint standardisé
 * Utile pour normaliser les appels inter-workers
 * 
 * @param {string} service - Le service ('ia', 'wa', 'config', 'chat')
 * @param {string} action - L'action ('text', 'vision', 'read', etc.)
 * @param {string} resource - La ressource (optionnel)
 * @returns {string} L'endpoint normalisé
 * 
 * @example
 * const endpoint = buildEndpoint('ia', 'text');
 * // Returns: '/api/ia/text'
 * 
 * @example
 * const endpoint = buildEndpoint('config', 'read', 'languages');
 * // Returns: '/api/config/read/languages'
 */
export function buildEndpoint(service, action, resource = null) {
  if (!service || !action) {
    return null;
  }

  const basePath = `/api/${service}/${action}`;
  return resource ? `${basePath}/${resource}` : basePath;
}

/**
 * Debug: Afficher toutes les routes disponibles
 * 
 * @param {Array<Object>} routesConfig - Configuration des routes
 * @returns {Array<string>} Liste des routes
 * 
 * @example
 * const routes = listAvailableRoutes(ROUTES_CONFIG);
 * console.log(routes);
 * // ['/api/ia/text', '/api/ia/vision', '/api/wa', ...]
 */
export function listAvailableRoutes(routesConfig) {
  if (!Array.isArray(routesConfig)) {
    return [];
  }

  return routesConfig
    .filter(config => config.route)
    .map(config => config.route);
}

/**
 * INTEGRATION AVEC ROUTES_CONFIG_BASE
 * ====================================
 * 
 * Les fonctions suivantes facilitent l'utilisation avec routes-config-base.js
 * Elles permettent de construire des configurations enrichies à partir de la base.
 */

/**
 * Résoudre basé sur ROUTES_CONFIG_BASE + enrichment
 * Utilise automatiquement workers-config.js si baseUrls n'est pas fourni
 * 
 * @param {string} endpoint - L'endpoint API
 * @param {Array<Object>} baseConfig - ROUTES_CONFIG_BASE (ou variante)
 * @param {Object} enrichment - Propriétés à ajouter {baseUrls?, icons?, circuitConfig?}
 * @returns {Object|null} Configuration du worker ou null
 * 
 * @example
 * import { resolveByBase } from './routes-resolver.js';
 * import { ROUTES_CONFIG_BASE } from './routes-config-base.js';
 * 
 * // Sans baseUrls : utilise workers-config.js automatiquement
 * const config = resolveByBase('/api/ia/text/generate', ROUTES_CONFIG_BASE, {
 *   circuitConfig: { failureThreshold: 3 }
 * });
 * 
 * // Avec baseUrls explicites : utilise les valeurs fournies
 * const config = resolveByBase('/api/ia/text/generate', ROUTES_CONFIG_BASE, {
 *   baseUrls: { ia: 'http://custom:65432' },
 *   circuitConfig: { failureThreshold: 3 }
 * });
 */
export function resolveByBase(endpoint, baseConfig, enrichment = {}) {
  if (!endpoint || !Array.isArray(baseConfig)) {
    return null;
  }

  // Trouver la route de base
  const baseRoute = resolveWorkerConfig(endpoint, baseConfig);
  if (!baseRoute) {
    return null;
  }

  // Enrichir avec les propriétés du contexte
  let { baseUrls = null, circuitConfig, icons } = enrichment;

  // Si baseUrls n'est pas fourni, utiliser workers-config.js automatiquement
  if (!baseUrls) {
    baseUrls = getWorkersBaseUrls('auto');
  }

  // Mapper le type de route à sa clé d'URL
  const typeToUrlKey = {
    TEXT: 'ia',
    VISION: 'ia',
    TRANSLATION: 'ia',
    WA: 'wa',
    CONFIG: 'config',
    CHAT: 'chat'
  };

  // Trouver la config de base complète
  const fullBaseRoute = baseConfig.find(r => r.route === baseRoute.route);
  if (!fullBaseRoute) {
    return baseRoute;
  }

  const urlKey = typeToUrlKey[fullBaseRoute.type];
  const baseUrl = baseUrls[urlKey] || baseRoute.baseUrl;

  const result = {
    ...baseRoute,
    baseUrl
  };

  // Ajouter circuitRoute si disponible
  if (baseRoute.circuitRoute) {
    result.circuitRoute = baseRoute.circuitRoute;
  }

  return result;
}

/**
 * Construire une config complète à partir de la base
 * Utilise automatiquement workers-config.js si baseUrls n'est pas fourni
 * 
 * @param {string} type - Type de route (TEXT, CONFIG, etc.)
 * @param {Array<Object>} baseConfig - ROUTES_CONFIG_BASE
 * @param {Object} context - Contexte {baseUrls?, icons?, circuitConfig?}
 * @returns {Object|null} Configuration complète du worker ou null
 * 
 * @example
 * import { buildFromBase } from './routes-resolver.js';
 * import { ROUTES_CONFIG_BASE } from './routes-config-base.js';
 * 
 * // Avec workers-config.js automatique
 * const config = buildFromBase('TEXT', ROUTES_CONFIG_BASE, {
 *   icons: { TEXT: faFileLines }
 * });
 * 
 * // Ou avec baseUrls personnalisées
 * const config = buildFromBase('TEXT', ROUTES_CONFIG_BASE, {
 *   baseUrls: { ia: 'http://custom:65432' },
 *   icons: { TEXT: faFileLines }
 * });
 */
export function buildFromBase(type, baseConfig, context = {}) {
  if (!type || !Array.isArray(baseConfig)) {
    return null;
  }

  const baseRoute = baseConfig.find(r => r.type === type);
  if (!baseRoute) {
    return null;
  }

  let { baseUrls = null, circuitConfig, icons } = context;

  // Si baseUrls n'est pas fourni, utiliser workers-config.js automatiquement
  if (!baseUrls) {
    baseUrls = getWorkersBaseUrls('auto');
  }

  // Mapper le type à sa clé d'URL
  const typeToUrlKey = {
    TEXT: 'ia',
    VISION: 'ia',
    TRANSLATION: 'ia',
    WA: 'wa',
    CONFIG: 'config',
    CHAT: 'chat'
  };

  const urlKey = typeToUrlKey[type];
  const baseUrl = baseUrls[urlKey];

  const result = {
    ...baseRoute,
    baseUrl
  };

  // Ajouter icon si disponible
  if (icons && icons[type]) {
    result.serviceIcon = icons[type];
  }

  // Ajouter circuitBreakerConfig si disponible
  if (circuitConfig) {
    result.circuitBreakerConfig = circuitConfig;
  }

  // Ajouter circuitRoute (réutilise la route comme circuit route)
  result.circuitRoute = baseRoute.route;

  return result;
}

/**
 * Obtenir la config complète pour un endpoint
 * Combine route resolution avec récupération de la config de base
 * 
 * @param {string} endpoint - L'endpoint API
 * @param {Array<Object>} fullConfig - Configuration complète enrichie (ROUTES_CONFIG)
 * @returns {Object|null} Configuration complète du worker ou null
 * 
 * @example
 * import { ROUTES_CONFIG } from './routes-config.js';
 * import { getConfigForEndpoint } from './routes-resolver.js';
 * 
 * const fullConfig = getConfigForEndpoint('/api/ia/text/generate', ROUTES_CONFIG);
 * // Returns: { route, type, baseUrl, workerName, circuitRoute, circuitBreakerConfig, ... }
 */
export function getConfigForEndpoint(endpoint, fullConfig) {
  if (!endpoint || !Array.isArray(fullConfig)) {
    return null;
  }

  // Les endpoints /api/rag/* sont routés via /api/chat (même worker: wab-chatbot)
  let effectiveEndpoint = endpoint;
  if (endpoint.startsWith('/api/rag/')) {
    effectiveEndpoint = endpoint.replace('/api/rag/', '/api/chat/');
  }

  // Chercher la route complète qui correspond
  const sortedRoutes = [...fullConfig].sort((a, b) => {
    const routeA = a.route || '';
    const routeB = b.route || '';
    return routeB.length - routeA.length;
  });

  for (const config of sortedRoutes) {
    const route = config.route || '';
    if (effectiveEndpoint.startsWith(route)) {
      return config;  // Retourner la config complète
    }
  }

  return null;
}

/**
 * Export par défaut pour commodité
 */
export default {
  getWorkersBaseUrls,
  resolveWorkerConfig,
  resolveRouteByType,
  isValidEndpoint,
  extractRouteType,
  buildEndpoint,
  listAvailableRoutes,
  resolveByBase,
  buildFromBase,
  getConfigForEndpoint
};
